import type { SelectionChangedEvent } from 'ag-grid-community'
import { useCallback } from 'react'

const AG_GRID_UI_SELECT_ALL_SOURCES = [
  'uiSelectAll',
  'uiSelectAllFiltered',
  'uiSelectAllCurrentPage',
  'keyboardSelectAll',
] as const

const isUiSelectAllSource = (source: string | undefined) =>
  AG_GRID_UI_SELECT_ALL_SOURCES.includes(source as (typeof AG_GRID_UI_SELECT_ALL_SOURCES)[number])

export type AgGridSelectionEventType = 'row-selected' | 'all-selected' | 'all-deselected'

export type AgGridBulkSelectionConfig = {
  onSelectionChanged?: (ids: string[]) => void
  onSelectionEvent?: (type: AgGridSelectionEventType, count: number) => void
}

export const mapAgGridRowsToIds = <T extends { id: string }>(rows: T[]): string[] =>
  rows.map((row) => row.id)

export const mapAgGridRowsToDefinedIds = <T extends { id?: string }>(rows: T[]): string[] =>
  rows.map((row) => row.id).filter((id): id is string => Boolean(id))

type UseAgGridSelectionChangedOptions<T> = {
  selection?: AgGridBulkSelectionConfig
  getSelectedIds: (rows: T[]) => string[]
  onSelectedCountChange?: (selectedCount: number) => void
}

export const useAgGridSelectionChanged = <T>({
  selection,
  getSelectedIds,
  onSelectedCountChange,
}: UseAgGridSelectionChangedOptions<T>) =>
  useCallback(
    (event: SelectionChangedEvent<T>) => {
      const selectedRows = event.api.getSelectedRows()
      const selectedCount = selectedRows.length
      const selectedIds = getSelectedIds(selectedRows)

      onSelectedCountChange?.(selectedCount)
      selection?.onSelectionChanged?.(selectedIds)

      const onSelectionEvent = selection?.onSelectionEvent
      if (!onSelectionEvent) {
        return
      }

      if (isUiSelectAllSource(event.source)) {
        onSelectionEvent(selectedCount > 0 ? 'all-selected' : 'all-deselected', selectedCount)
        return
      }

      if (event.source === 'checkboxSelected' || event.source === 'rowClicked') {
        onSelectionEvent('row-selected', selectedCount)
      }
    },
    [selection, getSelectedIds, onSelectedCountChange],
  )
